import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Eye, ShieldCheck, ShieldAlert, Crown, UserIcon, AlertTriangle, Copy, Inbox, RefreshCw,
  DollarSign, Users, TrendingUp, TrendingDown, Activity, Webhook, Download, Search,
  Sparkles, CreditCard, Gift, Wrench, CircleDot, ChevronRight, ShoppingBag, Clock,
} from "lucide-react";

type Row = {
  user_id: string;
  email: string | null;
  role: string;
  subscription_status: string | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  trial_end: string | null;
  canceled_at: string | null;
  effective_access_tier: string;
  has_active_override: boolean;
  override_expires_at: string | null;
  last_billing_webhook_processed_at_global: string | null;
  lifecycle_drift_flag: boolean;
  created_at: string | null;
  updated_at: string | null;
};

type ActionRow = { id: string; action_type: string; notes: string | null; created_at: string; admin_user_id: string; target_user_id: string };
type ParlayEntitlement = { id: string; user_id: string; parlay_id: string; created_at: string; expires_at: string };

// Estimated monthly Pro price for MRR calc — display only, doesn't affect billing.
const PRO_MONTHLY_PRICE_USD = 19.99;
const PARLAY_PRICE_USD = 9.99;

const mask = (v: string | null) => (v ? `${v.slice(0, 6)}••••${v.slice(-4)}` : "—");
const todayInputValue = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 10);
};
const endOfLocalDayIso = (dateValue: string) => {
  const [year, month, day] = dateValue.split("-").map(Number);
  return new Date(year, month - 1, day, 23, 59, 59, 999).toISOString();
};

function fmt(v: string | null) { return v ? new Date(v).toLocaleString() : "—"; }
function fmtDate(v: string | null) { return v ? new Date(v).toLocaleDateString() : "—"; }
function relTime(v: string | null) {
  if (!v) return "—";
  const diff = Date.now() - new Date(v).getTime();
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
}
function daysSince(v: string | null) {
  if (!v) return null;
  return Math.floor((Date.now() - new Date(v).getTime()) / 86_400_000);
}

type Tone = "green" | "amber" | "red" | "gray" | "blue" | "violet";
const toneClass: Record<Tone, string> = {
  green: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  amber: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  red: "bg-rose-500/15 text-rose-400 border-rose-500/30",
  gray: "bg-muted text-muted-foreground border-border",
  blue: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  violet: "bg-violet-500/15 text-violet-400 border-violet-500/30",
};

function ToneBadge({ tone, children, className }: { tone: Tone; children: React.ReactNode; className?: string }) {
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium", toneClass[tone], className)}>
      {children}
    </span>
  );
}

function accessLabel(r: Row): { label: string; tone: Tone } {
  if (r.role === "admin") return { label: "Admin", tone: "blue" };
  if (r.effective_access_tier === "pro") return { label: "Pro", tone: "green" };
  return { label: "Free", tone: "gray" };
}

function statusInfo(r: Row): { label: string; tone: Tone } {
  const s = r.subscription_status;
  if (!s || s === "none") return { label: "No Subscription", tone: "gray" };
  if (s === "active") return { label: "Active", tone: "green" };
  if (s === "trialing") return { label: "Trialing", tone: "blue" };
  if (s === "past_due") return { label: "Past Due", tone: "amber" };
  if (s === "unpaid") return { label: "Unpaid", tone: "red" };
  if (s === "canceled") return { label: "Canceled", tone: "red" };
  return { label: s, tone: "gray" };
}

function monetizationSource(r: Row, isParlayBuyer: boolean): { label: string; tone: Tone; icon: React.ReactNode } {
  if (r.role === "admin") return { label: "Admin Role", tone: "blue", icon: <Crown className="h-3 w-3" /> };
  if (r.has_active_override) return { label: "Admin Override", tone: "violet", icon: <Gift className="h-3 w-3" /> };
  if (r.effective_access_tier === "pro") return { label: "Stripe Pro", tone: "green", icon: <CreditCard className="h-3 w-3" /> };
  if (isParlayBuyer) return { label: "Parlay Buyer", tone: "amber", icon: <ShoppingBag className="h-3 w-3" /> };
  return { label: "Free", tone: "gray", icon: <UserIcon className="h-3 w-3" /> };
}

// Lifecycle stages for visualization
const LIFECYCLE_STAGES = ["trialing", "active", "past_due", "canceled"] as const;
function LifecycleBar({ status, cancelAtPeriodEnd }: { status: string | null; cancelAtPeriodEnd: boolean | null }) {
  const idx = status ? LIFECYCLE_STAGES.indexOf(status as any) : -1;
  return (
    <div className="flex items-center gap-1">
      {LIFECYCLE_STAGES.map((stage, i) => {
        const active = i <= idx;
        const current = i === idx;
        const tone = stage === "canceled" || stage === "past_due" ? "bg-rose-500" : stage === "trialing" ? "bg-sky-500" : "bg-emerald-500";
        return (
          <div key={stage} className="flex items-center gap-1">
            <div className={cn("h-1.5 w-6 rounded-full transition-all", active ? tone : "bg-muted", current && "ring-2 ring-offset-1 ring-offset-background ring-foreground/20")} title={stage} />
          </div>
        );
      })}
      {cancelAtPeriodEnd && <span className="text-[10px] text-amber-400 ml-1">⚠ cancels EOP</span>}
    </div>
  );
}

// Sparkline from daily counts
function Sparkline({ data, tone = "green", height = 28 }: { data: number[]; tone?: "green" | "amber" | "red" | "blue"; height?: number }) {
  if (data.length === 0) return <div className="text-[10px] text-muted-foreground">no data</div>;
  const max = Math.max(...data, 1);
  const w = 100;
  const stepX = w / Math.max(data.length - 1, 1);
  const points = data.map((v, i) => `${i * stepX},${height - (v / max) * (height - 4) - 2}`).join(" ");
  const stroke = { green: "hsl(160 84% 50%)", amber: "hsl(40 95% 55%)", red: "hsl(355 80% 60%)", blue: "hsl(200 90% 55%)" }[tone];
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full" preserveAspectRatio="none" style={{ height }}>
      <polyline points={points} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function AdminUsers() {
  const { isAdmin } = useAuth();
  const [rows, setRows] = useState<Row[]>([]);
  const [parlayEnts, setParlayEnts] = useState<ParlayEntitlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Row | null>(null);
  const [actions, setActions] = useState<ActionRow[]>([]);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");
  const [status, setStatus] = useState("all");
  const [segment, setSegment] = useState("all");
  const [grantOpen, setGrantOpen] = useState(false);
  const [revokeOpen, setRevokeOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [expiry, setExpiry] = useState("");
  const [reconciling, setReconciling] = useState(false);

  async function load() {
    setLoading(true);
    const [snap, ents] = await Promise.all([
      supabase.rpc("get_admin_user_subscription_snapshot" as any),
      supabase.from("parlay_purchase_entitlements" as any).select("id, user_id, parlay_id, created_at, expires_at").order("created_at", { ascending: false }).limit(1000),
    ]);
    if (snap.error) toast.error(snap.error.message);
    setRows(((snap.data ?? []) as Row[]).sort((a, b) => (a.email ?? "").localeCompare(b.email ?? "")));
    setParlayEnts(((ents.data ?? []) as unknown) as ParlayEntitlement[]);
    setLoading(false);
  }

  async function loadActions(uid: string) {
    const { data, error } = await supabase
      .from("admin_user_actions" as any)
      .select("id, action_type, notes, created_at, admin_user_id, target_user_id")
      .eq("target_user_id", uid)
      .order("created_at", { ascending: false })
      .limit(20);
    if (error) return;
    setActions(((data ?? []) as unknown) as ActionRow[]);
  }

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  const parlayBuyersByUser = useMemo(() => {
    const m = new Map<string, ParlayEntitlement[]>();
    for (const e of parlayEnts) {
      if (!m.has(e.user_id)) m.set(e.user_id, []);
      m.get(e.user_id)!.push(e);
    }
    return m;
  }, [parlayEnts]);

  // Business KPIs
  const kpis = useMemo(() => {
    const activePro = rows.filter((r) => r.effective_access_tier === "pro" && r.role !== "admin").length;
    const stripePaid = rows.filter((r) => r.subscription_status === "active" && !r.has_active_override).length;
    const trialing = rows.filter((r) => r.subscription_status === "trialing").length;
    const pastDue = rows.filter((r) => ["past_due", "unpaid"].includes(r.subscription_status ?? "")).length;
    const canceled = rows.filter((r) => r.subscription_status === "canceled").length;
    const totalUsers = rows.filter((r) => r.role !== "admin").length;
    const everSubscribed = rows.filter((r) => r.stripe_customer_id).length;

    const conversionRate = totalUsers > 0 ? (activePro / totalUsers) * 100 : 0;
    const churnRate = everSubscribed > 0 ? (canceled / everSubscribed) * 100 : 0;
    const trialConversion = trialing + activePro > 0 ? (activePro / (trialing + activePro)) * 100 : 0;

    const todayStart = new Date(); todayStart.setHours(0, 0, 0, 0);
    const parlaysToday = parlayEnts.filter((e) => new Date(e.created_at) >= todayStart).length;
    const parlaysMonth = parlayEnts.filter((e) => Date.now() - new Date(e.created_at).getTime() < 30 * 86_400_000).length;

    const mrr = stripePaid * PRO_MONTHLY_PRICE_USD;
    const parlayRevenueMonth = parlaysMonth * PARLAY_PRICE_USD;

    return { mrr, activePro, stripePaid, conversionRate, churnRate, trialConversion, parlaysToday, parlayRevenueMonth, totalUsers, trialing, pastDue, canceled };
  }, [rows, parlayEnts]);

  // Trends — last 14 days
  const trends = useMemo(() => {
    const days = 14;
    const buckets = (extract: (r: Row) => string | null) => {
      const arr = new Array(days).fill(0);
      const now = new Date(); now.setHours(0, 0, 0, 0);
      for (const r of rows) {
        const v = extract(r);
        if (!v) continue;
        const d = Math.floor((now.getTime() - new Date(v).setHours(0, 0, 0, 0)) / 86_400_000);
        if (d >= 0 && d < days) arr[days - 1 - d]++;
      }
      return arr;
    };
    const parlayBuckets = () => {
      const arr = new Array(days).fill(0);
      const now = new Date(); now.setHours(0, 0, 0, 0);
      for (const e of parlayEnts) {
        const d = Math.floor((now.getTime() - new Date(e.created_at).setHours(0, 0, 0, 0)) / 86_400_000);
        if (d >= 0 && d < days) arr[days - 1 - d]++;
      }
      return arr;
    };
    return {
      signups: buckets((r) => r.created_at),
      churn: buckets((r) => r.canceled_at),
      parlays: parlayBuckets(),
      revenue: parlayBuckets().map((c) => c * PARLAY_PRICE_USD),
    };
  }, [rows, parlayEnts]);

  // Needs attention items
  const attention = useMemo(() => {
    const items: Array<{ severity: Tone; title: string; count: number; description: string }> = [];
    const pastDue = rows.filter((r) => ["past_due", "unpaid"].includes(r.subscription_status ?? ""));
    if (pastDue.length) items.push({ severity: "red", title: "Failed payments", count: pastDue.length, description: "Subscriptions past due or unpaid" });
    const drift = rows.filter((r) => r.lifecycle_drift_flag);
    if (drift.length) items.push({ severity: "red", title: "Lifecycle drift", count: drift.length, description: "Stripe and local subscription state out of sync" });
    const expiredOverrides = rows.filter((r) => r.has_active_override && r.override_expires_at && new Date(r.override_expires_at) < new Date());
    if (expiredOverrides.length) items.push({ severity: "amber", title: "Expired overrides", count: expiredOverrides.length, description: "Override flagged active but past expiry" });
    const cancelEOP = rows.filter((r) => r.cancel_at_period_end && r.subscription_status === "active");
    if (cancelEOP.length) items.push({ severity: "amber", title: "Canceling at period end", count: cancelEOP.length, description: "Will downgrade at next renewal" });
    const inactivePro = rows.filter((r) => r.effective_access_tier === "pro" && r.role !== "admin" && r.updated_at && (Date.now() - new Date(r.updated_at).getTime() > 30 * 86_400_000));
    if (inactivePro.length) items.push({ severity: "amber", title: "Inactive Pro users", count: inactivePro.length, description: "No activity in 30+ days" });
    const lastWebhook = rows[0]?.last_billing_webhook_processed_at_global;
    if (lastWebhook && Date.now() - new Date(lastWebhook).getTime() > 24 * 3600_000) {
      items.push({ severity: "amber", title: "Stale webhook stream", count: 1, description: `Last billing webhook ${relTime(lastWebhook)}` });
    }
    return items;
  }, [rows]);

  const filtered = useMemo(() => rows.filter((r) => {
    if (tier !== "all") {
      if (tier === "admin" && r.role !== "admin") return false;
      if (tier !== "admin" && r.effective_access_tier !== tier) return false;
    }
    if (status !== "all" && (r.subscription_status ?? "none") !== status) return false;

    if (segment !== "all") {
      const ageDays = daysSince(r.created_at) ?? 9999;
      const updDays = daysSince(r.updated_at) ?? 9999;
      const hasParlay = parlayBuyersByUser.has(r.user_id);
      const isPro = r.effective_access_tier === "pro";
      if (segment === "new" && ageDays > 7) return false;
      if (segment === "active_today" && updDays > 1) return false;
      if (segment === "parlay_buyers" && !hasParlay) return false;
      if (segment === "churned" && r.subscription_status !== "canceled") return false;
      if (segment === "never_converted" && (r.stripe_customer_id || isPro)) return false;
      if (segment === "high_value") {
        const ent = parlayBuyersByUser.get(r.user_id) ?? [];
        const ltv = (isPro && ageDays > 0 ? Math.max(1, Math.floor(ageDays / 30)) * PRO_MONTHLY_PRICE_USD : 0) + ent.length * PARLAY_PRICE_USD;
        if (ltv < 50) return false;
      }
      if (segment === "drift" && !r.lifecycle_drift_flag) return false;
      if (segment === "overrides" && !r.has_active_override) return false;
    }

    if (q) {
      const needle = q.toLowerCase();
      const hay = [r.email, r.user_id, r.stripe_customer_id, r.stripe_subscription_id].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  }), [rows, tier, status, segment, q, parlayBuyersByUser]);

  function userLtv(r: Row) {
    const ageDays = daysSince(r.created_at) ?? 0;
    const months = Math.max(0, Math.floor(ageDays / 30));
    const ent = parlayBuyersByUser.get(r.user_id) ?? [];
    const subRev = r.effective_access_tier === "pro" && r.role !== "admin" ? Math.max(1, months) * PRO_MONTHLY_PRICE_USD : 0;
    return subRev + ent.length * PARLAY_PRICE_USD;
  }

  async function grantOverride() {
    if (!selected) return;
    if (!reason.trim()) return toast.error("Reason is required");
    const endsAt = expiry ? endOfLocalDayIso(expiry) : null;
    if (endsAt && new Date(endsAt) <= new Date()) return toast.error("Expiry must be in the future");
    const { error } = await supabase.rpc("admin_grant_subscription_override" as any, {
      _target_user_id: selected.user_id,
      _override_tier: "pro",
      _reason: reason.trim(),
      _ends_at: endsAt,
      _notes: "Granted from admin users dashboard",
    });
    if (error) return toast.error(error.message);
    toast.success("Pro override granted — does not modify Stripe");
    setGrantOpen(false); setReason(""); setExpiry("");
    await load();
    await loadActions(selected.user_id);
  }

  async function revokeOverride() {
    if (!selected) return;
    const { data: currentRaw } = await supabase
      .from("user_subscription_overrides" as any)
      .select("id")
      .eq("user_id", selected.user_id)
      .is("revoked_at", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    const current = (currentRaw as unknown) as { id: string } | null;
    if (!current?.id) return toast.error("No active override found");
    const { error } = await supabase.rpc("admin_revoke_subscription_override" as any, {
      _override_id: current.id,
      _notes: "Revoked from admin users dashboard",
    });
    if (error) return toast.error(error.message);
    toast.success("Override revoked");
    setRevokeOpen(false);
    await load();
    await loadActions(selected.user_id);
  }

  async function reconcileStripe() {
    if (!selected) return;
    setReconciling(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error("Admin session expired. Please sign in again.");
      const { data, error } = await supabase.functions.invoke("reconcile_stripe_subscription", {
        headers: { Authorization: `Bearer ${accessToken}` },
        body: { target_user_id: selected.user_id },
      });
      if (error) throw error;
      const result = data as { reconciled?: boolean; status?: string | null; reason?: string };
      if (result?.reconciled === false) {
        toast.warning(result.reason === "no_stripe_customer" ? "No Stripe customer linked to this user" : "Nothing to reconcile");
      } else {
        toast.success(`Reconciled — Stripe status: ${result?.status ?? "n/a"}`);
      }
      await load();
      const refreshed = (await supabase.rpc("get_admin_user_subscription_snapshot" as any)).data as Row[] | null;
      const next = refreshed?.find((r) => r.user_id === selected.user_id) ?? null;
      if (next) setSelected(next);
      await loadActions(selected.user_id);
    } catch (e: any) {
      toast.error(e?.message ?? "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  }

  function openUser(r: Row) { setSelected(r); loadActions(r.user_id); }
  function copy(v: string | null, label: string) {
    if (!v) return;
    navigator.clipboard.writeText(v).then(() => toast.success(`${label} copied`));
  }

  function exportCsv(scope: "all" | "revenue" | "subscriptions") {
    const data = filtered;
    let headers: string[] = [];
    let lines: string[][] = [];
    if (scope === "revenue") {
      headers = ["email", "user_id", "tier", "ltv_usd", "parlay_purchases", "stripe_customer_id"];
      lines = data.map((r) => {
        const ent = parlayBuyersByUser.get(r.user_id) ?? [];
        return [r.email ?? "", r.user_id, r.effective_access_tier, userLtv(r).toFixed(2), String(ent.length), r.stripe_customer_id ?? ""];
      });
    } else if (scope === "subscriptions") {
      headers = ["email", "user_id", "subscription_status", "current_period_start", "current_period_end", "cancel_at_period_end", "stripe_customer_id", "stripe_subscription_id"];
      lines = data.map((r) => [r.email ?? "", r.user_id, r.subscription_status ?? "", r.current_period_start ?? "", r.current_period_end ?? "", String(!!r.cancel_at_period_end), r.stripe_customer_id ?? "", r.stripe_subscription_id ?? ""]);
    } else {
      headers = ["email", "user_id", "role", "tier", "status", "has_override", "drift", "created_at", "ltv_usd"];
      lines = data.map((r) => [r.email ?? "", r.user_id, r.role, r.effective_access_tier, r.subscription_status ?? "", String(r.has_active_override), String(r.lifecycle_drift_flag), r.created_at ?? "", userLtv(r).toFixed(2)]);
    }
    const csv = [headers, ...lines].map((row) => row.map((c) => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `aleaedge-${scope}-${todayInputValue()}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${lines.length} rows`);
  }

  const lastWebhook = rows[0]?.last_billing_webhook_processed_at_global ?? null;
  const webhookStaleHours = lastWebhook ? (Date.now() - new Date(lastWebhook).getTime()) / 3600_000 : null;
  const webhookHealthy = webhookStaleHours !== null && webhookStaleHours < 24;

  return (
    <div className="container py-6 md:py-8 space-y-6">
      {/* Header */}
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-primary" />
            <h1 className="font-display text-3xl md:text-4xl font-bold">Operations & Revenue</h1>
          </div>
          <p className="text-sm text-muted-foreground flex items-center gap-2 mt-1">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-400" />
            Admin actions never modify Stripe lifecycle. MRR is an estimate based on ${PRO_MONTHLY_PRICE_USD}/mo Pro.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="secondary" onClick={() => exportCsv("all")}><Download className="h-3.5 w-3.5 mr-1" />Users CSV</Button>
          <Button size="sm" variant="secondary" onClick={() => exportCsv("revenue")}><Download className="h-3.5 w-3.5 mr-1" />Revenue CSV</Button>
          <Button size="sm" variant="secondary" onClick={() => exportCsv("subscriptions")}><Download className="h-3.5 w-3.5 mr-1" />Subs CSV</Button>
          <Button size="sm" variant="ghost" onClick={load} disabled={loading}><RefreshCw className={cn("h-3.5 w-3.5 mr-1", loading && "animate-spin")} />Refresh</Button>
        </div>
      </header>

      {/* Hero KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label="Estimated MRR"
          value={`$${kpis.mrr.toLocaleString()}`}
          sub={`${kpis.stripePaid} paying Stripe subs`}
          icon={<DollarSign className="h-4 w-4" />}
          tone="green"
          chart={<Sparkline data={trends.revenue} tone="green" />}
        />
        <KpiCard
          label="Active Pro Users"
          value={kpis.activePro.toLocaleString()}
          sub={`of ${kpis.totalUsers} total`}
          icon={<Crown className="h-4 w-4" />}
          tone="blue"
          chart={<Sparkline data={trends.signups} tone="blue" />}
        />
        <KpiCard
          label="Parlay Revenue (30d)"
          value={`$${kpis.parlayRevenueMonth.toFixed(0)}`}
          sub={`${kpis.parlaysToday} today`}
          icon={<ShoppingBag className="h-4 w-4" />}
          tone="amber"
          chart={<Sparkline data={trends.parlays} tone="amber" />}
        />
        <KpiCard
          label="Conversion Rate"
          value={`${kpis.conversionRate.toFixed(1)}%`}
          sub={`Trial→Paid: ${kpis.trialConversion.toFixed(0)}%`}
          icon={<TrendingUp className="h-4 w-4" />}
          tone="violet"
        />
      </div>

      {/* Secondary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <MiniKpi label="Trialing" value={kpis.trialing} icon={<CircleDot className="h-3.5 w-3.5" />} tone="blue" />
        <MiniKpi label="Past Due" value={kpis.pastDue} icon={<AlertTriangle className="h-3.5 w-3.5" />} tone={kpis.pastDue ? "red" : "gray"} />
        <MiniKpi label="Churned" value={kpis.canceled} icon={<TrendingDown className="h-3.5 w-3.5" />} tone="amber" sub={`${kpis.churnRate.toFixed(1)}% rate`} />
        <MiniKpi label="Overrides" value={rows.filter((r) => r.has_active_override).length} icon={<Gift className="h-3.5 w-3.5" />} tone="violet" />
        <MiniKpi label="Admins" value={rows.filter((r) => r.role === "admin").length} icon={<Crown className="h-3.5 w-3.5" />} tone="gray" />
        <MiniKpi label="Drift" value={rows.filter((r) => r.lifecycle_drift_flag).length} icon={<Activity className="h-3.5 w-3.5" />} tone={rows.some((r) => r.lifecycle_drift_flag) ? "red" : "gray"} />
      </div>

      {/* Needs Attention + Webhook Health */}
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="te-card p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-amber-400" />Needs Attention</h2>
            <span className="text-xs text-muted-foreground">{attention.length} open {attention.length === 1 ? "item" : "items"}</span>
          </div>
          {attention.length === 0 ? (
            <div className="text-sm text-muted-foreground py-6 text-center flex flex-col items-center gap-2">
              <ShieldCheck className="h-6 w-6 text-emerald-500" />
              All clear — no operational issues detected.
            </div>
          ) : (
            <ul className="space-y-2">
              {attention.map((a, i) => (
                <li key={i} className={cn("flex items-center justify-between gap-3 rounded-lg border p-3", toneClass[a.severity])}>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{a.title}</div>
                    <div className="text-xs opacity-80 truncate">{a.description}</div>
                  </div>
                  <div className="text-2xl font-bold tabular-nums">{a.count}</div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="te-card p-4">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold flex items-center gap-2"><Webhook className="h-4 w-4 text-sky-400" />Billing Health</h2>
            <ToneBadge tone={webhookHealthy ? "green" : webhookStaleHours === null ? "gray" : "red"}>
              {webhookHealthy ? "Healthy" : webhookStaleHours === null ? "Unknown" : "Stale"}
            </ToneBadge>
          </div>
          <div className="space-y-3 text-sm">
            <div>
              <div className="text-xs text-muted-foreground">Last webhook processed</div>
              <div className="font-medium">{lastWebhook ? relTime(lastWebhook) : "—"}</div>
              <div className="text-[10px] text-muted-foreground">{fmt(lastWebhook)}</div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Lifecycle drift</div>
              <div className={cn("font-semibold text-base", rows.some((r) => r.lifecycle_drift_flag) ? "text-rose-400" : "text-emerald-400")}>
                {rows.filter((r) => r.lifecycle_drift_flag).length} affected
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">Sync window</div>
              <div className="font-medium">{webhookStaleHours !== null ? `${webhookStaleHours.toFixed(1)}h` : "—"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="te-card p-3 md:p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Email, UUID, Stripe customer/sub ID…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8"
            />
          </div>
          <Pick value={tier} onValueChange={setTier} items={[["all","All access"],["free","Free"],["pro","Pro"],["admin","Admin"]]} ph="Access" />
          <Pick value={status} onValueChange={setStatus} items={[["all","All status"],["none","No Subscription"],["active","Active"],["trialing","Trialing"],["past_due","Past Due"],["unpaid","Unpaid"],["canceled","Canceled"]]} ph="Status" />
          <Pick value={segment} onValueChange={setSegment} items={[["all","All segments"],["new","New (≤7d)"],["active_today","Active today"],["parlay_buyers","Parlay buyers"],["churned","Churned"],["never_converted","Never converted"],["high_value","High value (LTV ≥$50)"],["overrides","Has override"],["drift","Drift only"]]} ph="Segment" />
        </div>
        <div className="flex flex-wrap gap-1.5 text-xs">
          {[["all","All"],["new","New"],["active_today","Active today"],["parlay_buyers","Parlay buyers"],["high_value","High LTV"],["churned","Churned"],["never_converted","Never converted"]].map(([v, l]) => (
            <button key={v} onClick={() => setSegment(v)} className={cn("px-2.5 py-1 rounded-full border transition-colors", segment === v ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground hover:border-foreground/30")}>
              {l}
            </button>
          ))}
          <span className="ml-auto text-muted-foreground">{filtered.length} of {rows.length}</span>
        </div>
      </div>

      {/* Desktop table */}
      <div className="te-card p-0 overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card/95 backdrop-blur z-10">
              <tr className="border-b border-border">
                <Th>User</Th>
                <Th>Source</Th>
                <Th>Lifecycle</Th>
                <Th>LTV</Th>
                <Th>Renews</Th>
                <Th>Last Active</Th>
                <Th>Health</Th>
                <Th className="text-right pr-4"> </Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-border/60">
                    {Array.from({ length: 8 }).map((_, j) => (
                      <td key={j} className="p-2"><Skeleton className="h-5 w-full" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="p-10 text-center text-muted-foreground">
                    <Inbox className="h-8 w-8 mx-auto mb-2 opacity-60" />
                    No users match your filters.
                  </td>
                </tr>
              ) : filtered.map((r) => {
                const acc = accessLabel(r);
                const ent = parlayBuyersByUser.get(r.user_id) ?? [];
                const src = monetizationSource(r, ent.length > 0);
                const ltv = userLtv(r);
                return (
                  <tr key={r.user_id} className="border-b border-border/40 hover:bg-secondary/40 transition-colors cursor-pointer group" onClick={() => openUser(r)}>
                    <Td>
                      <div className="flex items-center gap-2.5">
                        <div className={cn("h-8 w-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold", r.role === "admin" ? "bg-blue-500/15 text-blue-400" : r.effective_access_tier === "pro" ? "bg-emerald-500/15 text-emerald-400" : "bg-muted text-muted-foreground")}>
                          {(r.email ?? "?").slice(0, 1).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate max-w-[260px] flex items-center gap-1.5">
                            {r.email ?? "—"}
                            <ToneBadge tone={acc.tone} className="ml-1">{acc.label}</ToneBadge>
                          </div>
                          <div className="text-[10px] text-muted-foreground/60 font-mono truncate max-w-[260px]">{r.user_id.slice(0, 8)}…</div>
                        </div>
                      </div>
                    </Td>
                    <Td>
                      <ToneBadge tone={src.tone}>{src.icon}{src.label}</ToneBadge>
                      {ent.length > 0 && <div className="text-[10px] text-muted-foreground mt-0.5">{ent.length} parlay{ent.length === 1 ? "" : "s"}</div>}
                    </Td>
                    <Td>
                      <LifecycleBar status={r.subscription_status} cancelAtPeriodEnd={r.cancel_at_period_end} />
                      <div className="text-[10px] text-muted-foreground mt-1">{statusInfo(r).label}</div>
                    </Td>
                    <Td className="font-semibold tabular-nums">{ltv > 0 ? `$${ltv.toFixed(0)}` : <span className="text-muted-foreground font-normal">—</span>}</Td>
                    <Td className="whitespace-nowrap text-xs">{fmtDate(r.current_period_end)}</Td>
                    <Td className="whitespace-nowrap text-xs text-muted-foreground">{relTime(r.updated_at)}</Td>
                    <Td>
                      {r.lifecycle_drift_flag ? (
                        <ToneBadge tone="red"><AlertTriangle className="h-3 w-3" />Drift</ToneBadge>
                      ) : r.has_active_override ? (
                        <ToneBadge tone="violet"><Gift className="h-3 w-3" />Override</ToneBadge>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-emerald-500 text-xs"><div className="h-1.5 w-1.5 rounded-full bg-emerald-500" />ok</span>
                      )}
                    </Td>
                    <Td className="text-right pr-4">
                      <ChevronRight className="h-4 w-4 text-muted-foreground/40 group-hover:text-foreground transition-colors inline" />
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28 w-full" />)
        ) : filtered.length === 0 ? (
          <div className="te-card p-8 text-center text-muted-foreground">
            <Inbox className="h-8 w-8 mx-auto mb-2 opacity-60" />
            No users match your filters.
          </div>
        ) : filtered.map((r) => {
          const acc = accessLabel(r);
          const ent = parlayBuyersByUser.get(r.user_id) ?? [];
          const src = monetizationSource(r, ent.length > 0);
          const ltv = userLtv(r);
          return (
            <button key={r.user_id} className="te-card p-4 w-full text-left space-y-2" onClick={() => openUser(r)}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium truncate flex items-center gap-1.5">{r.email ?? "—"}<ToneBadge tone={acc.tone}>{acc.label}</ToneBadge></div>
                  <div className="text-[10px] text-muted-foreground/60 font-mono truncate">{r.user_id.slice(0, 8)}…</div>
                </div>
                <div className="text-right">
                  <div className="font-bold tabular-nums">{ltv > 0 ? `$${ltv.toFixed(0)}` : "—"}</div>
                  <div className="text-[10px] text-muted-foreground">LTV</div>
                </div>
              </div>
              <LifecycleBar status={r.subscription_status} cancelAtPeriodEnd={r.cancel_at_period_end} />
              <div className="flex flex-wrap gap-1.5">
                <ToneBadge tone={src.tone}>{src.icon}{src.label}</ToneBadge>
                {r.has_active_override && <ToneBadge tone="violet">Override</ToneBadge>}
                {r.lifecycle_drift_flag && <ToneBadge tone="red">Drift</ToneBadge>}
              </div>
              <div className="text-xs text-muted-foreground flex justify-between">
                <span>Active {relTime(r.updated_at)}</span>
                <span>Renews {fmtDate(r.current_period_end)}</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Detail drawer */}
      <Sheet open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <SheetContent className="w-full sm:max-w-[620px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle>User Profile</SheetTitle>
            <SheetDescription>Full subscription, monetization, and audit history.</SheetDescription>
          </SheetHeader>
          {selected && (() => {
            const acc = accessLabel(selected);
            const st = statusInfo(selected);
            const ent = parlayBuyersByUser.get(selected.user_id) ?? [];
            const src = monetizationSource(selected, ent.length > 0);
            const ageDays = daysSince(selected.created_at);
            const ltv = userLtv(selected);
            return (
              <div className="space-y-5 mt-6 text-sm">
                {/* Identity */}
                <section className="te-card p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className={cn("h-12 w-12 rounded-full flex items-center justify-center shrink-0 text-base font-bold", selected.role === "admin" ? "bg-blue-500/15 text-blue-400" : selected.effective_access_tier === "pro" ? "bg-emerald-500/15 text-emerald-400" : "bg-muted text-muted-foreground")}>
                      {(selected.email ?? "?").slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate text-base">{selected.email ?? "—"}</div>
                      <div className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-mono">
                        <span className="truncate">{selected.user_id}</span>
                        <button onClick={() => copy(selected.user_id, "User ID")} className="hover:text-foreground"><Copy className="h-3 w-3" /></button>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <ToneBadge tone={acc.tone}>{acc.label}</ToneBadge>
                    <ToneBadge tone={src.tone}>{src.icon}{src.label}</ToneBadge>
                    <ToneBadge tone={st.tone}>{st.label}</ToneBadge>
                  </div>
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    <Stat label="LTV" value={`$${ltv.toFixed(0)}`} />
                    <Stat label="Account age" value={ageDays !== null ? `${ageDays}d` : "—"} />
                    <Stat label="Last active" value={relTime(selected.updated_at)} />
                  </div>
                </section>

                {/* Lifecycle */}
                <Section title="Subscription Lifecycle">
                  <LifecycleBar status={selected.subscription_status} cancelAtPeriodEnd={selected.cancel_at_period_end} />
                  <KV k="Status" v={<ToneBadge tone={st.tone}>{st.label}</ToneBadge>} />
                  <KV k="Current period" v={`${fmtDate(selected.current_period_start)} → ${fmtDate(selected.current_period_end)}`} />
                  <KV k="Cancels at period end" v={selected.cancel_at_period_end ? <ToneBadge tone="amber">Yes</ToneBadge> : "No"} />
                  <KV k="Trial end" v={fmtDate(selected.trial_end)} />
                  <KV k="Canceled at" v={fmt(selected.canceled_at)} />
                  <div className="pt-2">
                    <Button size="sm" variant="secondary" onClick={reconcileStripe} disabled={reconciling}>
                      <RefreshCw className={cn("h-3.5 w-3.5 mr-1", reconciling && "animate-spin")} />
                      {reconciling ? "Reconciling…" : "Reconcile Stripe Status"}
                    </Button>
                  </div>
                </Section>

                {/* Parlay purchases */}
                <Section title={`Parlay Purchases (${ent.length})`}>
                  {ent.length === 0 ? (
                    <div className="text-xs text-muted-foreground py-1">No parlay purchases.</div>
                  ) : (
                    <ul className="space-y-1.5 text-xs">
                      {ent.slice(0, 10).map((e) => (
                        <li key={e.id} className="flex justify-between border-l-2 border-amber-500/40 pl-2">
                          <span className="font-mono truncate">{e.parlay_id.slice(0, 8)}…</span>
                          <span className="text-muted-foreground">{fmtDate(e.created_at)}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>

                {/* Overrides */}
                <Section title="Admin Overrides" right={<ToneBadge tone="amber">Does not modify Stripe</ToneBadge>}>
                  <KV k="Active override" v={selected.has_active_override ? <ToneBadge tone="violet">Active</ToneBadge> : <span className="text-muted-foreground">None</span>} />
                  <KV k="Override expires" v={fmt(selected.override_expires_at)} />
                  <div className="flex flex-col sm:flex-row gap-2 pt-2">
                    <Button size="sm" onClick={() => setGrantOpen(true)}>
                      <ShieldCheck className="h-3.5 w-3.5 mr-1" />Grant Pro override
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => setRevokeOpen(true)} disabled={!selected.has_active_override}>
                      <ShieldAlert className="h-3.5 w-3.5 mr-1" />Revoke override
                    </Button>
                  </div>
                </Section>

                {/* Audit */}
                <Section title={`Audit Activity (${actions.length})`}>
                  {actions.length === 0 ? (
                    <div className="text-xs text-muted-foreground py-1">No recent admin actions.</div>
                  ) : (
                    <ul className="space-y-2 text-xs">
                      {actions.map((a) => (
                        <li key={a.id} className="flex items-start gap-2 border-l-2 border-border pl-2">
                          <div className="flex-1">
                            <div className="font-medium text-foreground">{a.action_type}</div>
                            <div className="text-muted-foreground">{a.notes ?? "—"}</div>
                          </div>
                          <div className="text-muted-foreground whitespace-nowrap">{relTime(a.created_at)}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </Section>

                {/* Technical */}
                <Section title="Technical">
                  <KV k="Stripe Customer" v={
                    <span className="font-mono text-xs flex items-center gap-1.5">
                      {mask(selected.stripe_customer_id)}
                      {selected.stripe_customer_id && <button onClick={() => copy(selected.stripe_customer_id, "Customer ID")} className="text-muted-foreground hover:text-foreground"><Copy className="h-3 w-3" /></button>}
                    </span>
                  } />
                  <KV k="Stripe Subscription" v={
                    <span className="font-mono text-xs flex items-center gap-1.5">
                      {mask(selected.stripe_subscription_id)}
                      {selected.stripe_subscription_id && <button onClick={() => copy(selected.stripe_subscription_id, "Subscription ID")} className="text-muted-foreground hover:text-foreground"><Copy className="h-3 w-3" /></button>}
                    </span>
                  } />
                  <KV k="User created" v={`${fmtDate(selected.created_at)} (${relTime(selected.created_at)})`} />
                  <KV k="Last updated" v={fmt(selected.updated_at)} />
                  <KV k="Last billing webhook" v={fmt(selected.last_billing_webhook_processed_at_global)} />
                  {selected.lifecycle_drift_flag && (
                    <div className="mt-2 rounded-md border border-rose-500/40 bg-rose-500/10 p-2.5 text-xs text-rose-300 flex items-start gap-2">
                      <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">Lifecycle drift detected</div>
                        <div className="opacity-80">Stripe and local subscription state are out of sync. Run reconcile.</div>
                      </div>
                    </div>
                  )}
                </Section>
              </div>
            );
          })()}
        </SheetContent>
      </Sheet>

      {/* Grant dialog */}
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Grant Pro Override</DialogTitle>
            <DialogDescription>Grants Pro access without modifying Stripe billing. Visible in audit log.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-muted-foreground">Reason (required)</label>
              <Input placeholder="e.g. Customer support comp — ticket #1234" value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Expiry (optional, defaults to never)</label>
              <Input type="date" min={todayInputValue()} value={expiry} onChange={(e) => setExpiry(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setGrantOpen(false)}>Cancel</Button>
            <Button onClick={grantOverride}>Grant override</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Revoke dialog */}
      <Dialog open={revokeOpen} onOpenChange={setRevokeOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke override?</DialogTitle>
            <DialogDescription>This only revokes the admin override and does not modify Stripe lifecycle fields.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRevokeOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={revokeOverride}>Revoke</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KpiCard({ label, value, sub, icon, tone, chart }: { label: string; value: string; sub?: string; icon: React.ReactNode; tone: Tone; chart?: React.ReactNode }) {
  return (
    <div className="te-card p-4 relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] text-muted-foreground uppercase tracking-wider font-medium">{label}</div>
          <div className="text-2xl md:text-3xl font-bold tabular-nums mt-1">{value}</div>
          {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
        </div>
        <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center", toneClass[tone])}>{icon}</div>
      </div>
      {chart && <div className="mt-3 -mx-1 opacity-80">{chart}</div>}
    </div>
  );
}

function MiniKpi({ label, value, icon, tone, sub }: { label: string; value: number; icon: React.ReactNode; tone: Tone; sub?: string }) {
  return (
    <div className="te-card p-3">
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</span>
        <span className={cn("h-5 w-5 rounded-md flex items-center justify-center", toneClass[tone])}>{icon}</span>
      </div>
      <div className="text-xl font-bold tabular-nums mt-0.5">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border/40 bg-background/40 p-2">
      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value}</div>
    </div>
  );
}

function Pick({ value, onValueChange, items, ph }: { value: string; onValueChange: (v: string) => void; items: [string, string][]; ph: string }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger><SelectValue placeholder={ph} /></SelectTrigger>
      <SelectContent>{items.map(([v, label]) => <SelectItem key={v} value={v}>{label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return <th className={cn("p-3 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider", className)}>{children}</th>;
}
function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn("px-3 py-2.5 align-middle", className)}>{children}</td>;
}

function Section({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section className="te-card p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</h3>
        {right}
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1 border-b border-border/40 last:border-0">
      <span className="text-xs text-muted-foreground">{k}</span>
      <span className="text-sm text-foreground text-right min-w-0 truncate max-w-[60%]">{v}</span>
    </div>
  );
}
